<script setup lang="ts">
import { useCurrentUser, useCurrentUserRole } from "@/api/current-user";
import { useCreateUser } from "@/api/user-query";
import { useSnackbarStore } from "@/stores";
import { mdiAccountPlus, mdiInformationOutline } from "@mdi/js";
import { useForm } from "@soft-db/form";
import { HTTPResponseError } from "@soft-db/http";
import {
	CreateUser,
	CreateUserAuth,
	Role,
	UserAuthType,
	UserScope,
} from "@soft-db/monitoring-api-client";
import { Card, FormField, Icon, InputGroup, Select, SelectOption, Switch } from "@soft-db/ui";
import { PropType } from "vue";
import { useI18n } from "vue-i18n";
import * as z from "zod";
import AppDialog from "../common/AppDialog.vue";
import PasswordField from "../common/PasswordField.vue";
import PermissionGuard from "../permission/PermissionGuard.vue";
import RoleField from "../users/RoleField.vue";
import UserFeatureToggle from "../users/UserFeatureToggle.vue";
import DistScope from "./components/DistScope.vue";
import SpaceScope from "./components/SpaceScope.vue";

const props = defineProps({
	scope: { type: Object as PropType<UserScope>, required: true },
});
const emit = defineEmits<{
	(e: "create", id: string): void;
}>();

const { t } = useI18n();

const snackbarStore = useSnackbarStore();

const dialog = defineModel<boolean>("dialog");

function close() {
	clear();
	dialog.value = false;
}

const authType = ref<UserAuthType>("email");

/**
 * Since we only have two type for now, we toggle it with a switch.
 * If we ever have other type (google, microsoft, etc.), we need to change this for a select box.
 */
function toggleAuthType() {
	if (authType.value === "email") {
		authType.value = "username";
	} else {
		authType.value = "email";
	}
}

const isEmailAuth = computed(() => authType.value === "email");
const isUsernameAuth = computed(() => authType.value === "username");

const { currentUser } = useCurrentUser();
const { hasRole } = useCurrentUserRole();

const userCreation = useCreateUser();

type CreateUserForm = Omit<CreateUser, "auth"> & {
	email?: string;
	username?: string;
	password?: string;
};
const { useField, handleSubmit, clear } = useForm<CreateUserForm>();

const name = useField("name", { schema: z.string().trim().min(4) });

const username = useField("username", { isRequired: () => isUsernameAuth.value, min: 4 });
const password = useField("password", { isRequired: () => isUsernameAuth.value, min: 8 });

const role = useField("role", { isRequired: () => isEmailAuth.value });

const { data: dashboard } = useField("features.dashboard", {
	isRequired: true,
	initialValue: true,
});
const { data: integrity } = useField("features.integrity", {
	isRequired: true,
	initialValue: true,
});

const availableRoles = computed<Role[]>(() => {
	if (currentUser.value == null) {
		return [];
	}

	let roles: Role[] = ["viewer", "editor", "admin"];

	if (
		props.scope.type === "distributor" &&
		props.scope.id === "2" &&
		currentUser.value?.role === "sys_admin"
	) {
		roles.push("sys_admin");
	}

	if (props.scope.type === currentUser.value?.scope.type) {
		roles = roles.filter((r) => hasRole(r));
	}

	return roles;
});

const email = useField("email", {
	schema: () => (isEmailAuth.value ? z.string().email() : undefined),
});

const preferredLang = useField("preferredLang", { isRequired: true });
const langs = computed(() => [
	{
		text: "Français (Canada)",
		value: "fr-CA",
	},
	{
		text: "English (Canada)",
		value: "en-CA",
	},
	{
		text: "English (United States)",
		value: "en-US",
	},
]);

const createUser = handleSubmit(async (user) => {
	if (!currentUser.value) return;

	if (isEmailAuth.value) {
		delete user.password;
	} else {
		delete user.email;
		user.role = "viewer";
	}

	let auth: CreateUserAuth;
	if (authType.value === "email" && user.email != null) {
		auth = {
			type: "email",
			email: user.email,
		};
	} else if (authType.value === "username" && user.username != null && user.password != null) {
		auth = {
			type: "username",
			username: user.username,
			password: user.password,
		};
	} else {
		// Shoulw not happen, by form rule
		return;
	}

	try {
		const newUser = await userCreation.mutateAsync({
			scope: props.scope,
			user: {
				...user,
				auth,
			},
		});

		snackbarStore.openOnMessage(t("snackbar.userCreated"));
		emit("create", newUser.id);
		close();
	} catch (e) {
		if (e instanceof HTTPResponseError) {
			const { response } = e;
			if ("statusCode" in response && response.statusCode === 409) {
				const msg = user.email != null ? t("users.alreadyExist") : t("users.genericAlreadyExist");
				snackbarStore.openOnError(msg);
			}
		}
	}
});
</script>

<template>
	<AppDialog
		v-model="dialog"
		class="w-[500px]"
		:actionText="t('common.create')"
		@close="close"
		@click:action="createUser"
	>
		<template #title>
			<Icon :d="mdiAccountPlus" />
			<span>{{ t("actions.createUser") }}</span>
		</template>
		<template #content>
			<SpaceScope v-if="scope.type === 'space'" :id="scope.id" />
			<DistScope v-if="scope.type === 'distributor'" :id="scope.id" />

			<PermissionGuard permission="space.create">
				<div v-if="scope.type === 'space'" class="flex justify-between">
					<FormField class="w-full" labelPosition="left" :label="t('actions.createWithEmail')">
						<Switch color="primary" :modelValue="isEmailAuth" @update:modelValue="toggleAuthType" />
					</FormField>
				</div>
			</PermissionGuard>

			<FormField
				v-if="isEmailAuth"
				class="w-full"
				:label="t('common.email')"
				:errors="email.errors.value"
			>
				<InputGroup class="w-full">
					<input v-model="email.data.value" />
				</InputGroup>
			</FormField>
			<template v-else>
				<FormField class="w-full" :label="t('common.username')" :errors="username.errors.value">
					<InputGroup class="w-full">
						<input v-model="username.data.value" />
					</InputGroup>
				</FormField>

				<PasswordField
					v-model="password.data.value"
					class="w-full"
					autocomplete="new-password"
					:label="t('common.password')"
					:errors="password.errors.value"
				/>
			</template>

			<RoleField
				:modelValue="isEmailAuth ? role.data.value : 'viewer'"
				:disabled="!isEmailAuth"
				:disabledReason="t('users.genericUserRoleDisabled')"
				:scope="scope.type"
				:errors="role.errors.value"
				:availableRoles="availableRoles"
				@update:modelValue="(r) => (role.data.value = r)"
			/>

			<FormField class="w-full" :label="t('profile.fullName')" :errors="name.errors.value">
				<InputGroup class="w-full">
					<input v-model="name.data.value" />
				</InputGroup>
			</FormField>

			<FormField class="w-full" :label="t('profile.language')" :errors="preferredLang.errors.value">
				<Select v-model="preferredLang.data.value" class="w-full">
					<SelectOption v-for="l of langs" :key="l.value" :value="l.value">
						{{ l.text }}
					</SelectOption>
				</Select>
			</FormField>

			<span class="text-xs">{{ t("space.features") }}</span>
			<div class="mt-1 flex flex-col">
				<UserFeatureToggle v-model="dashboard" feature="dashboard" />
				<UserFeatureToggle v-model="integrity" feature="integrity" />
			</div>

			<Card class="bg-info/20 p-3">
				<div class="flex items-center gap-2">
					<Icon :d="mdiInformationOutline" size="lg" />
					{{ t("user.featuresAfterCreation") }}
				</div>
			</Card>
		</template>
	</AppDialog>
</template>
